/**
 * HijriCalendar.impl.ts — implementation of the HijriCalendar interface
 * defined in `HijriCalendar.ts`.
 *
 * Loads:
 *   - src/data/islamic-holidays-2026-2030.json (55 occurrences pre-baked)
 *   - src/data/national-holidays-2026-2030.json (140 country/year keys)
 *
 * Pure data + computation; no network, no DOM. The 4 UI patterns
 * (HolidayCard) consume `getNextHoliday()` / `getEventsInRange()`.
 *
 * See: services/dispatch/strategic/ST2_Hijri_uncertainty_model.md
 */

import type {
  Country,
  HolidayId,
  PerYearOccurrence,
  IslamicHoliday,
  CalendarEvent,
  NextHolidayResult,
  CalendarRange,
  ResolvedEvent,
  HijriMethod,
  HijriCalendarOptions,
  HijriCalendar,
  UncertaintyKind,
} from './HijriCalendar';
import { gregorianToHijri } from './HijriDate';
import type { HijriDate as HijriDateT } from './HijriDate';

import islamicCatalog from '../data/islamic-holidays-2026-2030.json';
import nationalCatalog from '../data/national-holidays-2026-2030.json';

// ---------------------------------------------------------------------------
// Country tendency map. When `perCountry[user.country]` is empty, we use
// this hint to pick which date (primary or alternate) is "more likely" in
// the user's country. Source: regional sighting practice; flagship review
// 2026-05-07. Defaults are conservative — when in doubt, return primary.
// ---------------------------------------------------------------------------
const COUNTRY_TENDENCY: Partial<Record<Country, 'primary' | 'alternate'>> = {
  // Saudi/UAE/Qatar/Kuwait/Bahrain/Oman: follow Umm al-Qura → primary
  SA: 'primary', AE: 'primary', QA: 'primary', KW: 'primary', BH: 'primary', OM: 'primary',
  // Maghreb: Searchov 2026-05-15 verified Aladhan returns the Saudi UAQ
  // date for MA/DZ/TN at the current Dhū al-Ḥijjah boundary (Hajj season
  // is universally Saudi-aligned). The earlier 'alternate' default caused
  // the calendar grid + event marker to disagree by 1 day. Per-holiday
  // override system is a follow-up (RESEARCH-HIJRI-SOURCES-2026-05-15.md).
  MA: 'primary', DZ: 'primary', TN: 'primary',
  // Egypt: typically aligns with Saudi (primary)
  EG: 'primary',
  // Levant: mixed; use primary as conservative default
  JO: 'primary', LB: 'primary', SY: 'primary', IQ: 'primary', YE: 'primary',
  // Turkey: astronomical (Diyanet) → typically primary
  TR: 'primary',
  // SE/S Asia: Hajj boundary is Saudi-aligned per Aladhan (Searchov 2026-05-15).
  // Per-holiday tendency is the proper fix for Eid al-Fitr divergence — TODO.
  ID: 'primary', MY: 'primary', PK: 'primary', BD: 'primary', IN: 'primary',
  // Africa beyond Maghreb: Nigeria typically follows Saudi → primary
  NG: 'primary',
  // Western diaspora: heterogeneous; lean primary as Saudi-following majority
  GB: 'primary', FR: 'primary', DE: 'primary', US: 'primary', CA: 'primary', AU: 'primary',
};

interface IslamicCatalogShape {
  _meta: Record<string, unknown>;
  holidays: IslamicHoliday[];
}
interface NationalEntry {
  date: string;
  name: string;
  localName?: string;
  countryCode: string;
  fixed?: boolean;
  global?: boolean;
  type?: string;
  launchYear?: number | null;
}
interface NationalCatalogShape {
  _meta: Record<string, unknown>;
  holidays: Record<string, NationalEntry[]>;
}

const ISLAMIC = islamicCatalog as unknown as IslamicCatalogShape;
const NATIONAL = nationalCatalog as unknown as NationalCatalogShape;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function dayDiff(fromIso: string, toIso: string): number {
  const a = new Date(fromIso + 'T00:00:00Z').getTime();
  const b = new Date(toIso + 'T00:00:00Z').getTime();
  return Math.round((b - a) / 86400000);
}

/**
 * Deterministic-collapse for moon-sighting uncertainty.
 *
 * Why this exists: lunar months are 29 or 30 days. Once the authoritative
 * Hijri source (Aladhan HJCoSA cache → `aweni_hijri_auth_*` localStorage,
 * or `Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura')`) has committed a
 * specific Hijri day to a specific Gregorian day, the boundary it sits on
 * is no longer uncertain. The pre-baked catalog still carries both `primary`
 * (Saudi-UAQ tabular prediction) and `alternate` (±1 day fallback) because
 * those were generated months ago without knowing how the moon-sighting
 * attempts would resolve. Once one of them no longer matches the
 * authoritative Hijri for THIS Gregorian-day, it is structurally
 * impossible — drop it.
 *
 * Concrete example (2026-05-17): catalog says
 *   first_dhul_hijjah 1447: primary=2026-05-18, alternate=2026-05-17.
 * Today's authoritative Hijri (Intl islamic-umalqura) commits
 *   2026-05-17 = 30 Dhū al-Qaʿdah 1447 (not 1 Dhū al-Ḥijjah).
 * So `alternate` is no longer plausible — we drop it, and every consumer
 * (calendar grid, HolidayCard, getNextHoliday) gets a single deterministic
 * date for 1 Dhū al-Ḥijjah.
 *
 * Falls back to the original occurrence when:
 *   - the event is not moon-sighting (nothing to collapse);
 *   - the occurrence is already confirmed via `perCountry` (manual override
 *     wins);
 *   - both candidates fail validation (Hijri year outside Intl's
 *     islamic-umalqura table — should not happen for the 1447-1451 catalog).
 *
 * Pure function — no I/O, safe to call in render paths.
 */
function candidateMatchesHijri(
  gregorianIso: string,
  hijriYear: number,
  hijriMonth: number,
  hijriDay: number,
): boolean {
  // Build noon-UTC instant from the catalog's local-ISO so gregorianToHijri's
  // own TZ-normalization (HijriDate.ts Path 1.5) returns deterministic output.
  const d = new Date(`${gregorianIso}T12:00:00Z`);
  if (isNaN(d.getTime())) return false;
  const h = gregorianToHijri(d);
  return h.year === hijriYear && h.month === hijriMonth && h.day === hijriDay;
}

function collapseMoonSightingUncertainty(
  occ: PerYearOccurrence,
  hijriYear: number,
  hijriDate: { month: number; day: number },
  uncertaintyKind: UncertaintyKind,
): PerYearOccurrence {
  if (uncertaintyKind !== 'moon_sighting') return occ;
  if (occ.confirmed === true) return occ;
  if (!occ.alternate) return occ;

  const primaryValid = candidateMatchesHijri(occ.primary, hijriYear, hijriDate.month, hijriDate.day);
  const alternateValid = candidateMatchesHijri(occ.alternate, hijriYear, hijriDate.month, hijriDate.day);

  if (primaryValid && !alternateValid) {
    // Authoritative source has committed primary as the real Hijri date.
    // Mark `confirmed` so the UI uses the certainty-tier rendering (Pattern C),
    // not the "Most likely / Alternate" Pattern B framing.
    return { ...occ, alternate: undefined, confirmed: true };
  }
  if (!primaryValid && alternateValid) {
    return { ...occ, primary: occ.alternate, alternate: undefined, confirmed: true };
  }
  // Both valid (impossible with Intl committing 1 Hijri per Gregorian) — or
  // both invalid (Hijri year outside Intl coverage). Either way: keep the
  // original ±1 uncertainty so the UI stays honest.
  return occ;
}
function resolveEffectiveDate(occ: PerYearOccurrence, country: Country, method: HijriMethod): string {
  // 1. Confirmed perCountry override wins.
  const country_ = occ.perCountry?.[country];
  if (country_) return country_;
  // 2. Method drives fallback choice.
  if (method === 'umm_al_qura') return occ.primary;
  if (method === 'astronomical') return occ.primary; // catalog primary IS Umm al-Qura tabular
  // sighting_local: use country tendency → primary or alternate
  const tend = COUNTRY_TENDENCY[country];
  if (tend === 'alternate' && occ.alternate) return occ.alternate;
  return occ.primary;
}

class HijriCalendarImpl implements HijriCalendar {
  constructor(private opts: HijriCalendarOptions) {}

  setCountry(country: Country): void {
    this.opts = { ...this.opts, country };
  }
  setMethod(method: HijriMethod): void {
    this.opts = { ...this.opts, method };
  }
  setLocale(locale: 'en' | 'fr' | 'ar' | 'es' | 'de'): void {
    this.opts = { ...this.opts, locale };
  }

  today(): HijriDateT {
    return gregorianToHijri();
  }
  toHijri(date: Date): HijriDateT {
    return gregorianToHijri(date);
  }

  getNextHoliday(now: Date = new Date()): NextHolidayResult | null {
    const today = isoDate(now);
    let best: NextHolidayResult | null = null;
    for (const h of ISLAMIC.holidays) {
      for (const yearStr of Object.keys(h.occurrences)) {
        const occ = collapseMoonSightingUncertainty(
          h.occurrences[yearStr],
          parseInt(yearStr, 10),
          h.hijriDate,
          h.uncertaintyKind,
        );
        const eff = resolveEffectiveDate(occ, this.opts.country, this.opts.method);
        const dDelta = dayDiff(today, eff);
        if (dDelta < 0) continue; // past
        if (!best || dDelta < best.daysAway) {
          best = {
            holiday: h,
            occurrence: occ,
            effectiveDate: eff,
            daysAway: dDelta,
            uiPattern: this.resolveUiPattern(occ, dDelta),
            isConfirmed: occ.confirmed === true,
          };
        }
      }
    }
    return best;
  }

  getHoliday(id: HolidayId, hijriYear: number): {
    holiday: IslamicHoliday;
    occurrence: PerYearOccurrence;
    effectiveDate: string;
  } | null {
    const h = ISLAMIC.holidays.find((x) => x.id === id);
    if (!h) return null;
    const rawOcc = h.occurrences[String(hijriYear)];
    if (!rawOcc) return null;
    const occ = collapseMoonSightingUncertainty(rawOcc, hijriYear, h.hijriDate, h.uncertaintyKind);
    return {
      holiday: h,
      occurrence: occ,
      effectiveDate: resolveEffectiveDate(occ, this.opts.country, this.opts.method),
    };
  }

  getEventsInRange(from: Date, to: Date): CalendarRange {
    const fromIso = isoDate(from);
    const toIso = isoDate(to);
    const events: ResolvedEvent[] = [];

    // Islamic: include occurrence if (primary or alternate or perCountry) is in range
    for (const h of ISLAMIC.holidays) {
      for (const yearStr of Object.keys(h.occurrences)) {
        const occ = collapseMoonSightingUncertainty(
          h.occurrences[yearStr],
          parseInt(yearStr, 10),
          h.hijriDate,
          h.uncertaintyKind,
        );
        const eff = resolveEffectiveDate(occ, this.opts.country, this.opts.method);
        const altInRange = !!(occ.alternate && occ.alternate >= fromIso && occ.alternate <= toIso);
        const effInRange = eff >= fromIso && eff <= toIso;
        if (effInRange || altInRange) {
          const ce: CalendarEvent = {
            id: h.id,
            kind: 'islamic_holiday',
            hijriDate: h.hijriDate,
            uncertaintyKind: h.uncertaintyKind,
            occurrences: { [yearStr]: occ },
            countries: [],
            nameI18n: h.name,
            isPublicHoliday: h.isPublicHoliday,
            isReligious: true,
            source: 'umm_al_qura_tabular',
          };
          events.push({
            source: ce,
            primaryDate: eff,
            alternateDate: occ.confirmed ? undefined : occ.alternate,
            showAsRange: !occ.confirmed && h.uncertaintyKind === 'moon_sighting',
            isPast: eff < fromIso,
          });
        }
      }
    }

    // National: scan country/year keys overlapping the range
    if (this.opts.includeNationalHolidays && this.opts.country !== 'global') {
      for (let y = from.getFullYear(); y <= to.getFullYear(); y++) {
        const key = `${this.opts.country}_${y}`;
        const list = NATIONAL.holidays[key] || [];
        for (const ent of list) {
          if (ent.date >= fromIso && ent.date <= toIso) {
            const ce: CalendarEvent = {
              id: `nh_${this.opts.country}_${ent.date}_${ent.name.toLowerCase().replace(/\s+/g, '-')}`,
              kind: 'national_holiday',
              occurrences: {
                [String(y)]: { primary: ent.date, sources: ['nager-date'] },
              },
              countries: [this.opts.country],
              nameI18n: { [this.opts.locale]: ent.localName || ent.name } as Partial<Record<'en'|'fr'|'ar'|'es'|'de', string>>,
              isPublicHoliday: ent.type === 'Public',
              isReligious: false,
              source: 'nager-date',
            };
            events.push({
              source: ce,
              primaryDate: ent.date,
              showAsRange: false,
              isPast: ent.date < fromIso,
            });
          }
        }
      }
    }

    events.sort((a, b) => a.primaryDate.localeCompare(b.primaryDate));
    return { from: fromIso, to: toIso, events };
  }

  getSpecialDay(date: Date = new Date()): ResolvedEvent | null {
    const today = isoDate(date);
    let bestPriority = -1;
    let best: ResolvedEvent | null = null;
    const PRIORITY: Record<HolidayId, number> = {
      eid_fitr: 100,
      eid_adha: 100,
      arafah_day: 95,
      ramadan_start: 90,
      laylat_alqadr_27: 92,
      first_dhul_hijjah: 70,
      hijri_new_year: 70,
      ashura: 75,
      mawlid: 60,
      isra_miraj: 55,
      middle_shaaban: 50,
    };

    for (const h of ISLAMIC.holidays) {
      for (const yearStr of Object.keys(h.occurrences)) {
        const occ = collapseMoonSightingUncertainty(
          h.occurrences[yearStr],
          parseInt(yearStr, 10),
          h.hijriDate,
          h.uncertaintyKind,
        );
        const eff = resolveEffectiveDate(occ, this.opts.country, this.opts.method);
        if (eff !== today && occ.alternate !== today) continue;
        const prio = PRIORITY[h.id] ?? 0;
        if (prio > bestPriority) {
          bestPriority = prio;
          const ce: CalendarEvent = {
            id: h.id,
            kind: 'islamic_holiday',
            hijriDate: h.hijriDate,
            uncertaintyKind: h.uncertaintyKind,
            occurrences: { [yearStr]: occ },
            countries: [],
            nameI18n: h.name,
            isPublicHoliday: h.isPublicHoliday,
            isReligious: true,
            source: 'umm_al_qura_tabular',
          };
          best = {
            source: ce,
            primaryDate: eff,
            alternateDate: occ.confirmed ? undefined : occ.alternate,
            showAsRange: !occ.confirmed && h.uncertaintyKind === 'moon_sighting',
            isPast: false,
          };
        }
      }
    }
    return best;
  }

  resolveUiPattern(occurrence: PerYearOccurrence, daysAway: number): NextHolidayResult['uiPattern'] {
    if (occurrence.confirmed === true) return 'C_confirmed';
    if (daysAway > 30) return 'A_far_future';
    if (daysAway >= 0 && daysAway <= 30) return 'B_approaching';
    return 'A_far_future';
  }

  async reloadCatalog(): Promise<{ islamic: number; national: number }> {
    return {
      islamic: ISLAMIC.holidays.length,
      national: Object.keys(NATIONAL.holidays).length,
    };
  }
}

export function createHijriCalendar(options: HijriCalendarOptions): HijriCalendar {
  return new HijriCalendarImpl(options);
}

// Exported for test fixtures: peek into raw catalogs.
export const __TEST_ONLY_CATALOGS = { ISLAMIC, NATIONAL, COUNTRY_TENDENCY };

// Exported for unit tests that pin the deterministic-collapse algorithm.
export const __TEST_ONLY_COLLAPSE = { collapseMoonSightingUncertainty, candidateMatchesHijri };
